import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { userApi } from '@/domains/system-management/apis/user-management';
import type { Params, UserRequest } from '@/domains/system-management/types/user';
import NoticeMessage from '@/plugin/noticemessage/noticemessage';

export const useUserManagement = (params?: Params) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  // Get user list
  const useUsers = () => {
    return useQuery({
      queryKey: ['users', params],
      queryFn: () => userApi.getUsers(params ?? {}),
    });
  };

  // Get user details
  const useUser = (id: string) => {
    return useQuery({
      queryKey: ['user', id],
      queryFn: () => userApi.getUser(id),
      enabled: !!id,
    });
  };

  // Create user
  const useCreateUser = (onCreateSuccess?: (response: { id: string }) => void) => {
    return useMutation({
      mutationFn: (data: UserRequest) => userApi.createUser(data),
      onSuccess: (responseData) => {
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        new NoticeMessage(t('msg > registration success'), {
          callback() {
            queryClient.invalidateQueries({ queryKey: ['users', params] });
            if (onCreateSuccess) onCreateSuccess(responseData);
          },
        });
      },
      onError: (error: unknown) => {
        const axiosError = error as AxiosError<{ message: string }>;
        const message = axiosError.response?.data?.message || axiosError.message;
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        new NoticeMessage(t(message));
      },
    });
  };

  // Update user
  const useUpdateUser = (onUpdateSuccess?: () => void) => {
    return useMutation({
      mutationFn: (data: UserRequest) => userApi.updateUser(data),
      onSuccess: (responseData) => {
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        new NoticeMessage(t('msg > update success'), {
          callback() {
            queryClient.invalidateQueries({ queryKey: ['users', params] });
            queryClient.invalidateQueries({
              queryKey: ['user', responseData.id],
            });
            if (onUpdateSuccess) onUpdateSuccess();
          },
        });
      },
      onError: (error: unknown) => {
        const axiosError = error as AxiosError<{ message: string }>;
        const message = axiosError.response?.data?.message || axiosError.message;
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        new NoticeMessage(t(message));
      },
    });
  };

  // Delete user
  const useDeleteUser = (onDeleteSuccess?: () => void) => {
    return useMutation({
      mutationFn: (id: string) => userApi.deleteUser(id),
      onSuccess: () => {
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        new NoticeMessage(t('msg > delete success'), {
          callback() {
            queryClient.invalidateQueries({ queryKey: ['users', params] });
            if (onDeleteSuccess) onDeleteSuccess();
          },
        });
      },
      onError: (error: unknown) => {
        const axiosError = error as AxiosError<{ message: string }>;
        const message = axiosError.response?.data?.message || axiosError.message;
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        new NoticeMessage(t(message));
      },
    });
  };

  return {
    useUsers,
    useUser,
    useCreateUser,
    useUpdateUser,
    useDeleteUser,
  };
};
